import { JsonRpcProvider, BrowserProvider, Contract, formatUnits, parseUnits } from "ethers";
import {
  useAppKitProvider,
  useAppKitAccount,
} from "@reown/appkit/react";
import { useCallback } from "react";

import {
  KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
  REWARD_NETWORK_KIND_ABI,
  TOKEN_CONTRACT_ADDRESS,
  TOKEN_ABI,
} from "../contracts/contracts";

// ✅ RPC URL for BSC
const RPC_URL = "https://bsc-dataseed.bnbchain.org";

function useContract() {
  // ✅ AppKit hooks
  const { walletProvider } = useAppKitProvider("eip155");
  const { address, isConnected } = useAppKitAccount();

  // ---------------- PROVIDER ----------------
  const getProvider = useCallback(() => {
    if (!walletProvider) throw new Error("Wallet not connected");
    // ✅ RPC for reads + BrowserProvider for wallet signing
    return new JsonRpcProvider(RPC_URL);
  }, [walletProvider]);

  const getSigner = useCallback(async () => {
    if (!walletProvider) throw new Error("Wallet not connected");
    // ✅ Use BrowserProvider for proper wallet signing
    const walletProviderInstance = new BrowserProvider(walletProvider);
    return await walletProviderInstance.getSigner();
  }, [walletProvider]);

  const getContract = useCallback(async (contractAddress, abi) => {
    const signer = await getSigner();
    return new Contract(contractAddress, abi, signer);
  }, [getSigner]);

  // ---------------- TOKEN ----------------
  const approveToken = useCallback(async (amount) => {
    const contract = await getContract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI);
    const tx = await contract.approve(
      KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
      parseUnits(amount.toString(), 18)
    );
    await tx.wait();
    return tx.hash;
  }, [getContract]);

  const getBalance = useCallback(async () => {
    const contract = await getContract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI);
    const balance = await contract.balanceOf(address);
    return formatUnits(balance, 18);
  }, [getContract, address]);

  // ---------------- BUY PACKAGE ----------------
  const buyPackage = useCallback(async (pid, ref) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);

    const pkg = await contract.packages(pid);
    const price = pkg.price;

    const token = await getContract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI);
    const approveTx = await token.approve(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, price);
    await approveTx.wait();

    const tx = await contract.buyPackage(pid, ref);
    await tx.wait();

    return tx.hash;
  }, [getContract]);

  // ---------------- CLAIM ----------------
  const claimReward = useCallback(async (pid) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.claimReward(pid);
    await tx.wait();
    return tx.hash;
  }, [getContract]);

  // ---------------- TERMINATE ----------------
  const terminate = useCallback(async (index) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.terminate(index);
    await tx.wait();
    return tx.hash;
  }, [getContract]);

  // ---------------- AIRDROP ----------------
  const claimAirdrop = useCallback(async (pid, token) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.claimAirdrop(pid, token);
    await tx.wait();
    return tx.hash;
  }, [getContract]);
  
  const getAirdropTokens = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    return await contract.getAirdropTokens();
  }, [getContract]);

  // ---------------- READ FUNCTIONS ----------------
  const getUserDashboard = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const data = await contract.getUserDashboard(address);

    return {
      totalPackages: Number(data[0]),
      activePackages: Number(data[1]),
      totalSpent: formatUnits(data[2], 18),
      totalClaimed: formatUnits(data[3], 18),
      claimable: formatUnits(data[4], 18),
    };
  }, [getContract, address]);

  const getFullDashboard = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const data = await contract.getUserFullDashboard(address);

    return {
      totalPackages: Number(data[0]),
      activePackages: Number(data[1]),
      totalSpent: formatUnits(data[2], 18),
      totalClaimed: formatUnits(data[3], 18),
      claimable: formatUnits(data[4], 18),
      referralTotal: formatUnits(data[5], 18),
    };
  }, [getContract, address]);

  const getUserPackages = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    return await contract.getUserPackages(address);
  }, [getContract, address]);

  const pendingReward = useCallback(async (index) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const reward = await contract.pendingReward(index);
    return formatUnits(reward, 18);
  }, [getContract]);

  const totalClaimable = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const reward = await contract.totalClaimable(address);
    return formatUnits(reward, 18);
  }, [getContract, address]);

  // ---------------- REFERRAL ----------------
  const getReferralStats = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const res = await contract.getReferralStats(address);

    // 🔥 Normalize ALWAYS
    if (Array.isArray(res)) {
      // Case: [earnings, [levels]]
      if (Array.isArray(res[1])) {
        const levels = res[1];

        return {
          direct: Number(levels[0] || 0),
          team: levels.reduce((a, b) => a + Number(b || 0), 0),
        };
      }

      // Case: [direct, team]
      return {
        direct: Number(res[0] || 0),
        team: Number(res[1] || 0),
      };
    }

    return { direct: 0, team: 0 };
  }, [getContract, address]);

  const getReferralEarnings = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    return await contract.getReferralEarnings(address);
  }, [getContract, address]);

  // ---------------- ADMIN ----------------
  const isAdmin = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const owner = await contract.owner();
    return owner.toLowerCase() === address.toLowerCase();
  }, [getContract, address]);

  const createPackage = useCallback(async (name, price) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.createPackage(name, parseUnits(price.toString(), 18));
    await tx.wait();
  }, [getContract]);

  const setPackageStatus = useCallback(async (pid, status) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.setPackageStatus(pid, status);
    await tx.wait();
  }, [getContract]);

  const addAirdrop = useCallback(async (token, pid, amount) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);

    // ✅ approve the SAME token (NOT fixed token)
    const tokenContract = await getContract(token, TOKEN_ABI);

    const approveTx = await tokenContract.approve(
      KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
      parseUnits(amount.toString(), 18)
    );
    await approveTx.wait();

    const tx = await contract.addAirdrop(
      token,
      pid,
      parseUnits(amount.toString(), 18)
    );

    await tx.wait();
  }, [getContract]);

  const setWallets = useCallback(async (treasury, marketing) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.setWallets(treasury, marketing);
    await tx.wait();
  }, [getContract]);

  const setReferralStatus = useCallback(async (status) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.setReferralStatus(status);
    await tx.wait();
  }, [getContract]);

  const pause = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    await (await contract.pause()).wait();
  }, [getContract]);

  const unpause = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    await (await contract.unpause()).wait();
  }, [getContract]);

  const setAllowedAirdropToken = useCallback(async (token, status) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.setAllowedAirdropToken(token, status);
    await tx.wait();
    return tx.hash;
  }, [getContract]);

  const setTaxConfig = async (tax, burn, marketing, pool) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI); // ✅ Fixed

    const tx = await contract.setTaxConfig(
      Number(tax),
      Number(burn),
      Number(marketing),
      Number(pool)
    );

    await tx.wait();
  };

  // ✅ ADD THIS FUNCTION to your useContract hook
  const terminateShare = useCallback(async (index) => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const tx = await contract.terminateShare(index);
    await tx.wait();
    return tx.hash;
  }, [getContract]);

  // 🔥 FIXED GLOBAL FUNCTIONS
  const getGlobalStats = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const result = await contract.getGlobalStats();
    return result;
  }, [getContract]);

  const getCoreGlobalStats = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const result = await contract.getCoreGlobalStats();
    return result;
  }, [getContract]);

  const getGlobalRewardStats = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const result = await contract.getGlobalRewardStats();
    return result;
  }, [getContract]);

  const getSystemHealth = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    const result = await contract.getSystemHealth();
    return result;
  }, [getContract]);

  const getTotalReferralPaid = useCallback(async () => {
    const contract = await getContract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI);
    return await contract.totalReferralPaid();  // Public variable
  }, [getContract]);

  // ---------------- RETURN ----------------
  return {
    isConnected,
    address,

    approveToken,
    setAllowedAirdropToken,
    getBalance,

    buyPackage,
    terminate,

    claimReward,
    claimAirdrop,
    getAirdropTokens,

    getUserDashboard,
    getFullDashboard,
    getGlobalStats,
    getUserPackages,
    pendingReward,
    totalClaimable,
    setTaxConfig,

    getProvider,

    getReferralStats,
    getReferralEarnings,

    isAdmin,
    createPackage,
    setPackageStatus,
    addAirdrop,
    setWallets,
    setReferralStatus,
    pause,
    unpause,
    terminateShare,

    getGlobalStats,
    getCoreGlobalStats,
    getGlobalRewardStats,
    getSystemHealth,

    getTotalReferralPaid
  };
}

export default useContract;